import React, { Component } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { compose } from 'react-apollo'
import { Link, withRouter } from 'react-router-dom'
import { Row, Col, media } from 'styled-bootstrap-grid'
import { connect } from 'react-redux'

import theme from 'ui/styles/theme'
import getRelativeUrl from 'utils/getRelativeUrl'
import withNav from 'containers/withNav'
import { Relative } from 'ui/styles/utils'

import Logo from 'ui/components/generic/Logo'
import MenuIconComponent from 'ui/components/generic/MenuIcon'

const NavItemPropType = PropTypes.shape({
  id: PropTypes.string,
  label: PropTypes.string,
  url: PropTypes.string
})

const Wrapper = styled.div`
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100%;
  z-index: 20;
`

const TopBar = styled.div`
  position: relative;
  padding: 0 20px;
  height: ${props => props.theme.navHeight}px;
  background-color: ${props => (props.solid ? '#fff' : 'transparent')};
  transition: all 500ms;

  z-index: 2;

  /** Make container, row fill height **/
  > div,
  > div > div {
    height: 100%;
  }

  a {
    color: ${props => (props.solid ? '#000' : '#fff')};
    transition: all 500ms;
  }

  li:after {
    background-color: ${props => (props.solid ? '#000' : '#fff')};
    transition: all 500ms;
  }

  svg {
    path {
      fill: ${props => (props.solid ? '#000' : '#fff')};
      transition: all 500ms;
    }
  }
`

const MainNav = styled.ul`
  list-style: none;
  text-align: center;
  padding: 0;
  margin: 0;
  height: 100%;
  display: none;

  ${media.lg`
    display: block;
  `}
`

const NavCol = styled(Col)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: ${props => (props.fillHeight ? '100%' : 'auto')};
`

const LogoCol = styled(Col)`
  display: flex;
  align-items: center;
`

const NavItem = styled.li`
  display: inline-flex;
  position: relative;
  height: 100%;
  padding: 0 15px;
  align-items: center;

  ${media.xl`
    padding: 0 17px;
  `}

  a {
    text-decoration: none;
    font-family: ${props => props.theme.displayFont};
    transition: all 400ms;
    font-size: 30px;
  }

  &:after {
    display: block;
    content: '';
    position: absolute;
    transition: all 700ms;

    bottom: 13px;
    width: 15px;
    height: 1px;
    left: 50%;
    margin-left: -7.5px;

    background-color: #fff;

    opacity: ${props => (props.active ? 1 : 0)};
    transform: scale(${props => (props.active ? 1 : 0)}, 1);
  }
`

const MenuIcon = styled(MenuIconComponent)`
  display: flex;

  ${media.lg`
    display: none;
  `}
`

const Overlay = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: fixed;
  top: 0px;
  bottom: 0px;
  left: 0px;
  width: 100%;
  padding: ${props => props.theme.navHeight}px 20px;

  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;

  background-color: rgba(0, 0, 0, 0.75);
  color: #fff;
  opacity: ${props => (props.visible ? 1 : 0)};
  z-index: 1;
  pointer-events: ${props => (props.visible ? 'auto' : 'none')};

  transition: all 800ms;
  transition-delay: ${props => (props.visible ? 0 : 1000)}ms;
`

const OverlayNav = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 50px;

  > div {
    transform: translateX(${props => (props.visible ? 0 : -20)}px);
    opacity: ${props => (props.visible ? 1 : 0)};
    transition: all 400ms;
  }
`

const OverlayNavItem = styled.div`
  margin-bottom: 20px;

  font-size: 28px;
  line-height: 40px;
  text-align: center;

  && {
    transition-delay: ${props => 400 + props.index * 100}ms;
  }

  a {
    font-family: ${props => props.theme.displayFont};
    font-weight: 300;
    color: #fff;
  }
`

class Navigation extends Component {
  static propTypes = {
    mainNav: PropTypes.arrayOf(NavItemPropType).isRequired,
    secondaryNav: PropTypes.arrayOf(NavItemPropType).isRequired
  }

  static defaultProps = {
    mainNav: [],
    secondaryNav: []
  }

  state = { overlayVisible: false }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll)
    this.handleScroll()
  }

  componentDidUpdate(prevProps) {
    const { overlayVisible } = this.state

    if (
      overlayVisible &&
      prevProps.location.pathname !== this.props.location.pathname
    ) {
      this.setState({ overlayVisible: false })
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll')
  }

  handleScroll = () => {
    const { solid } = this.state

    const scrollBreakpoint = window.innerHeight - theme.navHeight

    if (window.scrollY > scrollBreakpoint && !solid) {
      this.setState({ solid: true })
    } else if (window.scrollY <= scrollBreakpoint && solid) {
      this.setState({ solid: false })
    }
  }

  closeOverlay = () => this.setState({ overlayVisible: false })

  render() {
    const { mainNav, location } = this.props

    const { overlayVisible, solid } = this.state

    return (
      <Wrapper>
        <Overlay visible={overlayVisible}>
          <Relative fillHeight>
            <OverlayNav visible={overlayVisible}>
              {mainNav.map((item, index) => (
                <OverlayNavItem
                  index={index}
                  key={item.id}
                  active={getRelativeUrl(item.url) === location.pathname}
                >
                  <Link to={getRelativeUrl(item.url)}>{item.label}</Link>
                  {item.childItems.length > 0 && (
                    <ul>
                      {item.childItems.map(child => (
                        <li>
                          <Link to={getRelativeUrl(child.url)}>
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </OverlayNavItem>
              ))}
              <OverlayNavItem key="shopLink" index={mainNav.length}>
                <a
                  href="https://shop.kajsavala.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Shop
                </a>
              </OverlayNavItem>
            </OverlayNav>
          </Relative>
        </Overlay>
        <TopBar solid={solid}>
          <Row alignItems="flex-start">
            <LogoCol xs={8} sm={2}>
              <Link to="/" onClick={this.closeOverlay}>
                <Logo />
              </Link>
            </LogoCol>

            <NavCol xs={4} sm={10} fillHeight>
              <MenuIcon
                toggle={() =>
                  this.setState(state => ({
                    overlayVisible: !state.overlayVisible
                  }))
                }
                open={overlayVisible}
                light={!solid}
              />
              <MainNav>
                {mainNav.map(item => (
                  <NavItem
                    key={item.id}
                    active={getRelativeUrl(item.url) === location.pathname}
                  >
                    <Link to={getRelativeUrl(item.url)}>{item.label}</Link>
                    {item.childItems.length > 0 && (
                      <ul>
                        {item.childItems.map(child => (
                          <li>
                            <Link to={getRelativeUrl(child.url)}>
                              {child.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </NavItem>
                ))}
                <NavItem key="shopLink">
                  <a
                    href="https://shop.kajsavala.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Shop
                  </a>
                </NavItem>
              </MainNav>
            </NavCol>
          </Row>
        </TopBar>
      </Wrapper>
    )
  }
}

export default compose(
  withRouter,
  withNav('MAIN_NAV', 'mainNav'),
  connect(state => ({ breakpoint: state.media.breakpoint }))
)(Navigation)



// WEBPACK FOOTER //
// ./src/ui/components/Navigation.js